import React, { useState } from "react";
import { View, Text, StyleSheet } from "react-native";
import {
  Button,
  BUTTON_TYPE,
  COLORS,
  TYPOGRAPHY,
  COMMON_STYLES,
  VARIANTS_STYLES,
  LazyImage,
} from "@dehaat/dds";
import ForecastModal from "./WeatherModal";
import {
  HourlyTemperature,
  HourlyWeather,
  TodayWeather,
  WeeklyTemperature,
  WeeklyWeather,
} from "../models/advistory";
import { useTranslation } from "react-i18next";

type ForecastType = {
  type: string;
  visible: boolean;
  title: string;
  note: string;
  data: HourlyTemperature[] | WeeklyTemperature[] | [];
  noteIcon: string;
};

const initialState: ForecastType = {
  type: "",
  visible: false,
  title: "",
  note: "",
  data: [],
  noteIcon: "",
};

const WeatherInfoItem = ({
  icon,
  label,
  value,
  unit,
}: {
  icon: string;
  label: string;
  value: string | number;
  unit: string;
}) => {
  if (!value && value !== 0) return null;
  return (
    <View
      style={[
        COMMON_STYLES.row,
        COMMON_STYLES["mb1/4"],
        COMMON_STYLES.alignItemsCenter,
      ]}
    >
      {icon && <LazyImage imageUrl={icon} imgStyles={styles.icon} />}
      <Text style={styles.text}>
        {label}: {value}
        {unit}
      </Text>
    </View>
  );
};

const WeatherForecastCard = ({
  data,
  hourlyWeather,
  weaklyWeather,
  location,
}: {
  data: TodayWeather;
  hourlyWeather: HourlyWeather;
  weaklyWeather: WeeklyWeather;
  location: string;
}) => {
  const { t } = useTranslation();
  const [forecastModal, setForecastModal] =
    useState<ForecastType>(initialState);

  const now = new Date();

  const currentTime = now.toLocaleString("en-US", {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const openModal = (type: "hourly" | "weekly") => {
    if (type === "hourly") {
      setForecastModal({
        type: "hourly",
        visible: true,
        title: "Hourly Forecast",
        note: hourlyWeather.alert,
        noteIcon: hourlyWeather.alert_icon,
        data: hourlyWeather.temparature,
      });
    } else {
      setForecastModal({
        type: "weekly",
        visible: true,
        title: "Weather for next 7 days",
        note: weaklyWeather.alert,
        noteIcon: hourlyWeather.alert_icon,
        data: weaklyWeather.temparature,
      });
    }
  };
  const closeModal = () => setForecastModal(initialState);

  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <LazyImage
          imageUrl={data?.weather_icon}
          imgStyles={styles.weatherIcon}
        />
        <View>
          <Text
            style={[
              TYPOGRAPHY.HEADING2,
              VARIANTS_STYLES.EMPHASIZED,
              { color: COLORS.neutral90 },
            ]}
          >
            {data?.temperature}°C
          </Text>
          {/* <Text style={styles.text}>
            {data?.max}°C | {data?.min}°C
          </Text> */}
        </View>
        <View style={COMMON_STYLES.ml1}>
          <Text
            style={[
              TYPOGRAPHY.HEADING2,
              VARIANTS_STYLES.EMPHASIZED,
              { color: COLORS.neutral90 },
            ]}
          >
            {data?.weather_type}
          </Text>
          <Text style={styles.text}>{currentTime}</Text>
        </View>
      </View>

      <View style={[COMMON_STYLES["mt1/2"]]}>
        <WeatherInfoItem
          icon={data?.rain_chance_percent?.icon}
          label={t("chances_of_rain")}
          value={data?.rain_chance_percent?.value}
          unit="%"
        />
        <WeatherInfoItem
          icon={data?.humidity_percent?.icon}
          label={t("humidity")}
          value={data?.humidity_percent?.value}
          unit="%"
        />
        <WeatherInfoItem
          icon={data?.wind?.icon}
          label={t("wind_speed")}
          value={data?.wind?.value}
          unit=""
        />
      </View>

      <View style={[COMMON_STYLES["mt1"], { gap: 10 }]}>
        {hourlyWeather && (
          <Button
            text={t("view_hourly_forecast")}
            type={BUTTON_TYPE.SECONDARY}
            onClick={() => openModal("hourly")}
          />
        )}
        {weaklyWeather && (
          <Button
            text={t("view_next_7_days")}
            type={BUTTON_TYPE.SECONDARY}
            onClick={() => openModal("weekly")}
          />
        )}
      </View>

      {forecastModal.visible && (
        <ForecastModal
          type={forecastModal.type}
          visible={forecastModal.visible}
          title={forecastModal.title}
          subTitle={location}
          data={forecastModal.data}
          alert={forecastModal.note}
          alertIcon={forecastModal.noteIcon}
          onClose={closeModal}
          rainIcon={data?.rain_chance_percent?.icon}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    borderRadius: 16,
    backgroundColor: COLORS.white,
    padding: 16,
    borderColor: COLORS.neutral20,
    borderWidth: 1,
    marginVertical: 10,
  },
  weatherEmoji: {
    fontSize: 50,
    marginRight: 12,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  text: {
    color: COLORS.neutral80,
    fontSize: 12,
  },
  minMax: {
    fontSize: 14,
    color: COLORS.neutral90,
  },

  sub: {
    fontSize: 13,
    color: COLORS.neutral80,
  },
  info: {
    fontSize: 14,
    marginBottom: 4,
    color: COLORS.neutral100,
  },
  modalContainer: {
    flex: 1,
    padding: 16,
    backgroundColor: COLORS.white,
  },
  modalHeading: {
    fontSize: 20,
    fontWeight: "700",
  },
  modalSub: {
    fontSize: 14,
    color: COLORS.neutral90,
    marginTop: 4,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderColor: COLORS.neutral20,
    alignItems: "center",
  },
  hourText: {
    fontSize: 14,
    color: COLORS.neutral100,
  },
  dayText: {
    fontSize: 15,
    color: COLORS.neutral100,
    flex: 1,
  },
  advisoryBox: {
    backgroundColor: COLORS.neutral20,
    padding: 12,
    marginVertical: 12,
    borderRadius: 8,
  },
  icon: {
    height: 20,
    width: 20,
    marginRight: 6,
  },
  weatherIcon: {
    height: 60,
    width: 60,
    resizeMode: "contain",
  },
});

export default WeatherForecastCard;
